import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { MastheadPortalId } from '../../../models/AdminModels';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { NavigationBreadcrumbsBar } from '../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../web/components/Portal';
import { PricingWizard } from '../../components/tools/PricingWizard';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';

const styles = StyleSheet.create({
	accountHeader: {
		backgroundColor: '#f8f9fa',
		borderBottom: '1px solid #e9ecef',
		marginBottom: 16,
		padding: '12px 16px',
	},
	accountNameRow: {
		alignItems: 'center',
		display: 'flex',
		gap: 12,
	},
	accountName: {
		color: '#212529',
		fontSize: 16,
		fontWeight: 600,
		margin: 0,
	},
	accountDetailsLink: {
		color: '#046cb6',
		fontSize: 14,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	accountLabel: {
		color: '#6c757d',
		fontSize: 12,
		marginBottom: 4,
	},
	accountInstructions: {
		color: '#495057',
		fontSize: 14,
		lineHeight: 1.5,
		marginTop: 8,
	},
});

export const AdminPricingWizardPage: React.FC = () => {
	const { accountId } = useParams<{ accountId?: string }>();

	const accountQuery = useAdminAccountQuery({
		accountId: accountId || '',
		enabled: !!accountId,
	});

	const accountName = accountQuery.data?.companyName;

	const pathComponentNameProvider = React.useCallback(
		(pathComponent: string) => {
			if (pathComponent === accountId && accountName) {
				return accountName;
			}
			if (pathComponent === 'pricing-wizard') {
				return 'Pricing Wizard';
			}
			return undefined;
		},
		[accountId, accountName]
	);

	return (
		<AdminDashboardPageLayout>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>
			{accountId && accountName && (
				<div className={css(styles.accountHeader)}>
					<div className={css(styles.accountLabel)}>Pricing for Account</div>
					<div className={css(styles.accountNameRow)}>
						<h2 className={css(styles.accountName)}>{accountName}</h2>
						<NavLink className={css(styles.accountDetailsLink)} to={`/accounts/${accountId}`}>
							Account Details
						</NavLink>
					</div>
					<p className={css(styles.accountInstructions)}>
						To finish setting up the account, configure the plan options below. Once you choose your plan, we&apos;ll
						provide a link to enter billing details.
					</p>
				</div>
			)}
			<PricingWizard accountId={accountId} />
		</AdminDashboardPageLayout>
	);
};
